'use client'

import React from 'react'
import { lodgeDestinationsData, adventuresData, experiencesData } from './headerData'
import { useActivityVisibility, extractIdFromUrl } from '@/hooks/useActivityVisibility'
import { useDynamicActivities } from '@/hooks/useDynamicActivities'

interface HeaderMobileSubmenusProps {
  mobileSubmenuOpen: string | null
  setMobileSubmenuOpen: (value: string | null) => void
  handleMobileMenuLinkClick: (e: React.MouseEvent, url: string) => void
  t: (key: string) => string
  tHeader: (key: string) => string
  tFooter: (key: string) => string
}

/**
 * Header Mobile Submenus Component
 * Mobile submenus for Lodges and Adventures
 */
export function HeaderMobileSubmenus({
  mobileSubmenuOpen,
  setMobileSubmenuOpen,
  handleMobileMenuLinkClick,
  t,
  tHeader,
  tFooter,
}: HeaderMobileSubmenusProps) {
  const { isVisible } = useActivityVisibility()
  const { activities: dynamicActivities } = useDynamicActivities()

  // Static adventure slugs for dedup
  const staticSlugs = new Set(adventuresData.map(a => extractIdFromUrl(a.url)))
  const dynamicOnly = dynamicActivities.filter(
    a => !staticSlugs.has(a.slug) && !staticSlugs.has(a.id)
  )

  return (
    <>
      {/* Mobile Submenu: Adventures */}
      <div className={`mobile-submenu ${mobileSubmenuOpen === 'adventures' ? 'active' : ''}`}>
        <div className="mobile-submenu-backdrop" />

        <div className="mobile-submenu-content">
          <div className="mobile-submenu-header">
            <button
              className="mobile-submenu-back"
              onClick={() => setMobileSubmenuOpen(null)}
              aria-label={tHeader('ariaBackToMenu')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>{t('back')}</span>
            </button>
          </div>

          <nav className="mobile-submenu-nav">
            {adventuresData.filter(a => isVisible(extractIdFromUrl(a.url), a.hidden)).map((adventure, idx) => (
              <a
                key={idx}
                href={adventure.url}
                className="mobile-submenu-link"
                onClick={(e) => handleMobileMenuLinkClick(e, adventure.url)}
              >
                {tFooter(adventure.nameKey)}
              </a>
            ))}
            {dynamicOnly.map((activity) => (
              <a
                key={activity.id}
                href={`/guided-adventures/${activity.slug}`}
                className="mobile-submenu-link"
                onClick={(e) => handleMobileMenuLinkClick(e, `/guided-adventures/${activity.slug}`)}
              >
                {activity.name}
              </a>
            ))}
          </nav>

          <div className="mobile-submenu-cta">
            <a
              href="/guided-adventures"
              className="mobile-submenu-view-all"
              onClick={(e) => handleMobileMenuLinkClick(e, '/guided-adventures')}
            >
              {t('viewAllAdventures')}
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Submenu: Lodges */}
      <div className={`mobile-submenu ${mobileSubmenuOpen === 'lodges' ? 'active' : ''}`}>
        <div className="mobile-submenu-backdrop" />

        <div className="mobile-submenu-content">
          <div className="mobile-submenu-header">
            <button
              className="mobile-submenu-back"
              onClick={() => setMobileSubmenuOpen(null)}
              aria-label={tHeader('ariaBackToMenu')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>{t('back')}</span>
            </button>
          </div>

          <nav className="mobile-submenu-nav">
            {lodgeDestinationsData.flatMap((region) =>
              region.lodges
                .filter(lodge => isVisible(extractIdFromUrl(lodge.url), lodge.comingSoon))
                .map((lodge, idx) => (
                  <a
                    key={`${region.regionKey}-${idx}`}
                    href={lodge.url}
                    className={`mobile-submenu-link${lodge.comingSoon ? ' coming-soon' : ''}`}
                    onClick={(e) => handleMobileMenuLinkClick(e, lodge.url)}
                  >
                    {tFooter(lodge.nameKey)}
                    {lodge.comingSoon && (
                      <span className="coming-soon-badge">
                        {tFooter('comingSoon')}
                      </span>
                    )}
                  </a>
                ))
            )}
          </nav>

          <div className="mobile-submenu-cta">
            <a
              href="/lodges"
              className="mobile-submenu-view-all"
              onClick={(e) => handleMobileMenuLinkClick(e, '/lodges')}
            >
              {t('viewAllLodges')}
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Submenu: Experiences */}
      <div className={`mobile-submenu ${mobileSubmenuOpen === 'experiences' ? 'active' : ''}`}>
        <div className="mobile-submenu-backdrop" />

        <div className="mobile-submenu-content">
          <div className="mobile-submenu-header">
            <button
              className="mobile-submenu-back"
              onClick={() => setMobileSubmenuOpen(null)}
              aria-label={tHeader('ariaBackToMenu')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              <span>{t('back')}</span>
            </button>
          </div>

          <nav className="mobile-submenu-nav">
            {experiencesData.filter(e => isVisible(extractIdFromUrl(e.url), e.hidden)).map((experience, idx) => (
              <a
                key={idx}
                href={experience.url}
                className="mobile-submenu-link"
                onClick={(e) => handleMobileMenuLinkClick(e, experience.url)}
              >
                {tFooter(experience.nameKey)}
              </a>
            ))}
          </nav>

          <div className="mobile-submenu-cta">
            <a
              href="/experiences"
              className="mobile-submenu-view-all"
              onClick={(e) => handleMobileMenuLinkClick(e, '/experiences')}
            >
              {t('viewAllExperiences')}
            </a>
          </div>
        </div>
      </div>
    </>
  )
}
